import { Component, Input, OnInit } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { User } from "../../../model/users";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { UsersService } from "../../../services/users.service";
import { AuthService } from "../../../auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Messages } from "../../../model/messages";
import { ActionButtonSubmit } from "../../../model/action-button-submit";
import { NavigationHelper } from "../../../services/navigation.helper";
import { Navigation } from "../../../model/navigation";

@Component({
  selector: "user-details",
  template: `
    <button class="back-button" (click)="goBack()">
      <i class="fa fa-arrow-left" aria-hidden="true"></i>
    </button>
  `,
  templateUrl: "./user-details.component.html",
  styleUrls: ["./user-details.component.css"],
})
export class UserDetailsComponent implements OnInit {
  user: BehaviorSubject<User>;
  editMode: boolean = false;
  userForm: FormGroup;
  userTitle: string = "userDetails.title.exist.user";
  userPassFormVisible: boolean = false;
  messages = new BehaviorSubject<Messages>(Messages.EMPTY);
  isLoading = false;
  supervisor: boolean = false;
  selectedUser: string = "";
  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private usersService: UsersService,
    private auth: AuthService,
    private router: Router,
    private nav: NavigationHelper
  ) {
    this.user = new BehaviorSubject<User>(User.EMPTY);
    this.usersService.selectFromRouter();
  }

  ngOnInit(): void {
    this.initializeForm();

    if (Navigation.NEW.toString() === this.nav.currentId) {
      this.editMode = true;
      this.userForm.reset();
      this.userTitle = "userDetails.title.new.user";
    }
    this.usersService.userObservable.subscribe((data) => {
      const selectedUser = data;
      this.messages.next(Messages.EMPTY);

      if (
        selectedUser &&
        selectedUser.name !== this.selectedUser &&
        this.userPassFormVisible
      ) {
        this.cancelPasswordChange();
      }

      if (selectedUser && selectedUser.id !== null) {
        this.populateFormWithUser(selectedUser);
        this.selectedUser = selectedUser.name;
      } else {
        this.user.next(User.EMPTY);
        this.userForm.reset();
      }
    });

    if (!(this.usersService.selected && this.usersService.selected.id)) {
      this.usersService.selectFromRouter();
    }
    this.supervisor = this.auth.isSupervisor();
  }

  private initializeForm(): void {
    this.userForm = this.fb.group({
      id: [{ value: 0, disabled: true }],
      name: "",
      displayname: "",
      email: "",
      supervisor: false,
      active: true,
    });
  }

  private populateFormWithUser(user: User): void {
    this.user.next(user);
    this.userForm.patchValue({
      id: user.id,
      name: user.name,
      displayname: user.displayname,
      email: user.email,
      supervisor: user.supervisor,
      active: user.active,
    });
    this.editMode = user.id === Navigation.NEW;
    this.userTitle = this.editMode
      ? "userDetails.title.new.user"
      : "userDetails.title.exist.user";
  }

  showUserPassForm() {
    this.userPassFormVisible = true;
  }

  cancelPasswordChange() {
    this.userPassFormVisible = false;
  }
  toggleEditMode() {
    if (this.editMode) {
      this.editMode = false;
      this.user.next(User.EMPTY);
      this.userForm.reset();
      this.messages.next(Messages.EMPTY);
      this.userTitle = "userDetails.title.exist.user";
    } else {
      this.editMode = true;
    }
  }

  UserEmpty(): boolean {
    const userValue = this.user.value;

    return (
      (!userValue || userValue.id === null || userValue.id === 0) &&
      !this.editMode
    );
  }

  saveUser(action: ActionButtonSubmit): void {
    this.isLoading = true;

    if (this.userForm.valid) {
      const token = this.auth.token;
      const id = this.user.value.id;

      const updatedUser = new User(
        id === null || id < 1 ? null : id,
        this.userForm.value.name || "",
        this.userForm.value.displayname || "",
        this.userForm.value.email || "",
        this.userForm.value.supervisor || false,
        this.userForm.value.active || false
      );

      if (updatedUser.hasValidDisplayName()) {
        if (updatedUser.id === null || updatedUser.id < 1) {
          this.usersService.createUser(token, updatedUser).subscribe(
            (messages) => {
              this.isLoading = false;
              this.messages.next(messages);

              if (messages.hasAnyMessages) {
                action.result(false);
              } else {
                this.updateUserList(token);
                this.router.navigate(["users"]);
                action.result(true);
              }
            },
            (error) => {
              this.isLoading = false;
              action.result(false);
              this.messages.next(
                Messages.from({ user: ["user.unknown.error", error] })
              );
            }
          );
        } else {
          this.usersService.updateUser(token, updatedUser).subscribe(
            (messages) => {
              this.isLoading = false;
              this.messages.next(messages);

              if (messages.hasAnyMessages) {
                action.result(false);
              } else {
                this.updateUserList(token);
                this.router.navigate(["users"]);
                action.result(true);
              }
            },
            (error) => {
              this.isLoading = false;
              action.result(false);
              this.messages.next(
                Messages.from({ user: ["user.unknown.error", error] })
              );
            }
          );
        }
      } else {
        this.isLoading = false;
        action.result(false);
        this.messages.next(
          Messages.from({ user: ["user.invalid.displayname"] })
        );
      }
    } else {
      this.isLoading = false;
      action.result(false);

      this.messages.next(Messages.from({ user: ["user.form.invalid"] }));
    }
  }

  deleteUser(): void {
    const token = this.auth.token;
    const userId = this.user.value.id;

    if (userId != null) {
      this.usersService.deleteUser(token, userId).subscribe(
        (response) => {
          this.messages.next(response);

          this.updateUserList(token);
          this.user.next(User.EMPTY);
        },
        (error) => {
          this.messages.next(Messages.from({ users: ["users.delete.failed"] }));
        }
      );
    }
  }

  updateUserList(token: string): void {
    this.usersService.users(token).subscribe((messages) => {
      const updatedUsers = this.usersService.usersData;
      this.usersService._currentUsers.next(updatedUsers);
    });
  }
  get TRUE(): boolean {
    return true;
  }
  get FALSE(): boolean {
    return false;
  }
  get isSupervisor(): boolean {
    return this.auth.userdata.supervisor;
  }
}
